import moment from "moment";

import { BATCH_STATUS, resolveManagementActionI18nKey } from "./echeqMassManageConstants";
import { getActionValue } from "./echeqMassManageUtils";

const STATUS_ALL = "TODOS";
const ACTION_ALL = "TODOS";
const DEFAULT_CURRENCY = "032";
const DEFAULT_ALLOWED_PERIOD_MONTHS = 6;
export const DEFAULT_LIST_ORDER_BY = "processDate";
export const DEFAULT_LIST_SORT_DIRECTION = "descendent";
const {
    PROCESSING: BATCH_STATUS_PROCESSING,
    FINISHED_SUCCESS: BATCH_STATUS_FINISHED_SUCCESS,
    FINISHED_ERROR: BATCH_STATUS_FINISHED_ERROR,
    FAILED: BATCH_STATUS_FAILED,
} = BATCH_STATUS;

export const CARD_LABEL_PREFIX = "echeqs.massives.manage.list.card";

export const BATCH_STATUS_FILTER_OPTIONS = [
    { value: BATCH_STATUS_PROCESSING },
    { value: BATCH_STATUS_FINISHED_SUCCESS },
    { value: BATCH_STATUS_FINISHED_ERROR },
    { value: BATCH_STATUS_FAILED },
];

const normalizeAmountValue = (amount) => {
    if (amount == null || amount === "") {
        return null;
    }
    if (typeof amount === "number") {
        return amount;
    }
    const normalized = String(amount)
        .replaceAll(".", "")
        .replaceAll(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? null : parsed;
};

const formatAmountParam = (amount) => {
    const normalized = normalizeAmountValue(amount);
    return normalized != null ? String(normalized) : null;
};

export function validateFilterAmountRange(value) {
    const { amountFrom } = this.parent;
    const from = normalizeAmountValue(amountFrom);
    const to = normalizeAmountValue(value);
    if (from == null || to == null) {
        return true;
    }
    return from <= to;
}

export const buildActionFilterOptions = (canAdmit, canPay, getFilterLabel, getActionLabel) => {
    const options = [{ value: ACTION_ALL, label: getFilterLabel("action.all") }];
    if (canAdmit) {
        options.push({ value: "ADMIT", label: getActionLabel("ADMIT") });
    }
    if (canPay) {
        options.push({ value: "PAY", label: getActionLabel("PAY") });
    }
    return options;
};

export const buildStatusFilterOptions = (getLabel) =>
    BATCH_STATUS_FILTER_OPTIONS.map((option) => ({
        value: option.value,
        label: getLabel(`status.${option.value}`),
    }));

const resolveActionFormValue = (actionCode, actionOptions = []) => {
    const allOption = actionOptions.find((item) => item.value === ACTION_ALL) || null;
    if (!actionCode || actionCode === ACTION_ALL) {
        return allOption;
    }
    return actionOptions.find((item) => item.value === actionCode) || allOption;
};

export const mapListFiltersToFormValues = (listFilters, { statusOptions = [], actionOptions = [] } = {}) => {
    const status = [];
    if (listFilters.status && listFilters.status !== STATUS_ALL) {
        String(listFilters.status)
            .split(",")
            .map((code) => code.trim())
            .filter(Boolean)
            .forEach((code) => {
                const option = statusOptions.find((item) => item.value === code);
                if (option) {
                    status.push(option);
                }
            });
    }

    const action = resolveActionFormValue(listFilters.action, actionOptions);

    const dateFrom = listFilters.dateFrom ? moment(listFilters.dateFrom, "YYYY-MM-DD").toDate() : null;
    const dateTo = listFilters.dateTo ? moment(listFilters.dateTo, "YYYY-MM-DD").toDate() : null;

    return {
        status,
        processDates: dateFrom && dateTo ? [dateFrom, dateTo] : undefined,
        reference: listFilters.reference || "",
        action,
        amountFrom: normalizeAmountValue(listFilters.amountFrom),
        amountTo: normalizeAmountValue(listFilters.amountTo),
        currency: listFilters.currency || DEFAULT_CURRENCY,
    };
};

export const mapFiltersFormValuesToListFilters = (formValues, pageNumber = 1) => {
    const statusValues = Array.isArray(formValues.status)
        ? formValues.status.map((item) => item?.value).filter((value) => value != null && value !== "")
        : [];
    const actionValue = getActionValue(formValues.action) || ACTION_ALL;
    const [dateFrom, dateTo] = formValues.processDates || [];

    return {
        status: statusValues.length ? statusValues.join(",") : STATUS_ALL,
        action: actionValue === ACTION_ALL ? ACTION_ALL : actionValue,
        dateFrom: dateFrom ? moment(dateFrom).format("YYYY-MM-DD") : null,
        dateTo: dateTo ? moment(dateTo).format("YYYY-MM-DD") : null,
        reference: String(formValues.reference ?? "").trim(),
        currency: formValues.currency || DEFAULT_CURRENCY,
        amountFrom: normalizeAmountValue(formValues.amountFrom),
        amountTo: normalizeAmountValue(formValues.amountTo),
        pageNumber,
    };
};

export const getDefaultListFilters = (allowedMonths = DEFAULT_ALLOWED_PERIOD_MONTHS) => {
    const today = moment().startOf("day");
    const from = today.clone().subtract(allowedMonths, "months");

    return {
        status: STATUS_ALL,
        action: ACTION_ALL,
        dateFrom: from.format("YYYY-MM-DD"),
        dateTo: today.format("YYYY-MM-DD"),
        reference: "",
        currency: DEFAULT_CURRENCY,
        amountFrom: null,
        amountTo: null,
        pageNumber: 1,
        orderBy: DEFAULT_LIST_ORDER_BY,
        direction: DEFAULT_LIST_SORT_DIRECTION,
    };
};

export const sumAmounts = (...values) => {
    const total = values.reduce((acc, value) => {
        if (value == null || value === "") {
            return acc;
        }
        const parsed = Number.parseFloat(String(value));
        return Number.isNaN(parsed) ? acc : acc + parsed;
    }, 0);
    return total > 0 ? String(total) : undefined;
};

export const mapBatchStatusToCardStatus = (status) => {
    if (status === BATCH_STATUS_PROCESSING) {
        return "in_progress";
    }
    if (status === BATCH_STATUS_FAILED) {
        return "failed";
    }
    if (status === BATCH_STATUS_FINISHED_ERROR) {
        return "processed_with_errors";
    }
    if (status === BATCH_STATUS_FINISHED_SUCCESS) {
        return "processed_success";
    }
    return "in_progress";
};

export const getCardStatusLabel = (cardStatus) => `${CARD_LABEL_PREFIX}.status.${cardStatus}`;

export const getCardLabel = (key) => `${CARD_LABEL_PREFIX}.${key}`;

export const mapBatchToCardProps = (batch, getLabels) => {
    const quantity = batch.quantity ?? 0;
    const cardStatus = mapBatchStatusToCardStatus(batch.status);
    const isInProgress = cardStatus === "in_progress";

    const amountARS = isInProgress ? undefined : sumAmounts(batch.arsAmountSuccessed, batch.arsAmountFailed);
    const amountUSD = isInProgress ? undefined : sumAmounts(batch.usdAmountSuccessed, batch.usdAmountFailed);

    const actionKey = resolveManagementActionI18nKey(batch.action);

    return {
        id: batch.idBatch,
        processDate: batch.processDate,
        batchId: batch.idBatch,
        actionLabel: actionKey ? getLabels(actionKey) : batch.action,
        batchReference: batch.reference || undefined,
        echeqCount: quantity,
        amountARS,
        amountUSD,
        status: cardStatus,
        statusLabel: getLabels(getCardStatusLabel(cardStatus)),
        getLabels: (key) => getLabels(getCardLabel(key)),
    };
};

export const buildListRequestPayload = (filters) => ({
    status: filters.status || STATUS_ALL,
    action: filters.action || ACTION_ALL,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    reference: filters.reference || "",
    currency: filters.currency || DEFAULT_CURRENCY,
    amountFrom: formatAmountParam(filters.amountFrom),
    amountTo: formatAmountParam(filters.amountTo),
    pageNumber: filters.pageNumber || 1,
    orderBy: filters.orderBy || DEFAULT_LIST_ORDER_BY,
    direction: filters.direction || DEFAULT_LIST_SORT_DIRECTION,
});
